import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Assessment as StatsIcon,
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { campaignAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '—';
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start.getTime() === 0 || end.getTime() === 0) {
    return 'Not Scheduled';
  }
  
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
};

const getSemanticStatusColor = (status) => {
  const colors = {
    draft: '#9ca3af',
    pending_approval: '#f59e0b',
    approved: '#3b82f6',
    active: '#22c55e',
    paused: '#f59e0b',
    completed: '#6b7280',
    rejected: '#dc2626',
  };
  return colors[status] || '#9ca3af';
};

const getSemanticStatusBg = (status) => {
  const bgColors = {
    draft: '#f3f4f6',
    pending_approval: '#fffbeb',
    approved: '#eff6ff',
    active: '#f0fdf4',
    paused: '#fffbeb',
    completed: '#f9fafb',
    rejected: '#fef2f2',
  };
  return bgColors[status] || '#f3f4f6';
};

const Campaigns = () => {
  const { admin: user, checkAuth } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openManualImpressionsDialog, setOpenManualImpressionsDialog] = useState(false);
  const [openMediaViewerDialog, setOpenMediaViewerDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [manualImpressions, setManualImpressions] = useState('');
  const [editFormData, setEditFormData] = useState({
    campaignName: '',
    clientName: '',
    startDate: '',
    endDate: '',
    offerNumber: '',
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (currentTab !== 'all') {
        params.status = currentTab;
      }
      const response = await campaignAPI.getAllCampaigns(params);
      const campaignsData = response.data?.data?.campaigns || response.data?.campaigns || response.data || [];
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [currentTab]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await campaignAPI.getCampaignStats();
      setStats(response.data?.data || response.data || null);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(null);
    }
  }, []);


  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [currentTab]);


  const handleOpenEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setEditFormData({
      campaignName: campaign.campaignName || '',
      clientName: campaign.clientName || '',
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
      offerNumber: campaign.offerNumber || '',
    });
    setOpenEditDialog(true);
  };

  const handleUpdateCampaign = async () => {
    try {
      await campaignAPI.updateCampaign(selectedCampaign._id, editFormData);
      toast.success('Campaign updated successfully');
      setOpenEditDialog(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to update campaign');
    }
  };

  const handleApproveCampaign = async () => {
    try {
      await campaignAPI.approveCampaign(selectedCampaign._id, {});
      toast.success('Campaign approved successfully');
      setOpenApproveDialog(false);
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error approving campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to approve campaign');
    }
  };

  const handleRejectCampaign = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await campaignAPI.rejectCampaign(selectedCampaign._id, { reason: rejectReason });
      toast.success('Campaign rejected');
      setOpenRejectDialog(false);
      setRejectReason('');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to reject campaign');
    }
  };

  const handlePauseCampaign = async (id) => {
    try {
      await campaignAPI.pauseCampaign(id);
      toast.success('Campaign paused');
      fetchCampaigns();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to pause campaign');
    }
  };

  const handleResumeCampaign = async (id) => {
    try {
      await campaignAPI.resumeCampaign(id);
      toast.success('Campaign resumed');
      fetchCampaigns();
    } catch (error) {
      console.error('Error resuming campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to resume campaign');
    }
  };

  const handleUpdateManualImpressions = async () => {
    if (!manualImpressions || isNaN(manualImpressions)) {
      toast.error('Please enter a valid number');
      return;
    }
    try {
      await campaignAPI.updateManualImpressions(selectedCampaign._id, {
        impressions: parseInt(manualImpressions),
      });
      toast.success('Impressions updated successfully');
      setOpenManualImpressionsDialog(false);
      setManualImpressions('');
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating impressions:', error);
      toast.error(error.response?.data?.message || 'Failed to update impressions');
    }
  };

  const handleDeleteCampaign = async () => {
    try {
      await campaignAPI.deleteCampaign(selectedCampaign._id);
      toast.success('Campaign deleted successfully');
      setOpenDeleteDialog(false);
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to delete campaign');
    }
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const canApprove = user?.role === "super_admin" || user?.role === "admin" || user?.permissions?.canApproveContent;


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Campaign Management
        </Typography>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalCampaigns}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Campaigns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.activeCampaigns}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Campaigns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.pendingApproval}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {stats.totalImpressions?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Impressions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          <Tab label="Draft" value="draft" />
          <Tab label="Pending Approval" value="pending_approval" />
          <Tab label="Approved" value="approved" />
          <Tab label="Active" value="active" />
          <Tab label="Paused" value="paused" />
          <Tab label="Completed" value="completed" />
          <Tab label="Rejected" value="rejected" />
        </Tabs>
      </Paper>

      {/* Campaigns Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'background.default',
                  '& .MuiTableCell-head': {
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    padding: '14px 16px',
                  },
                }}
              >
                <TableCell sx={{ width: '14%' }}>Offer Number</TableCell>
                <TableCell sx={{ width: '18%' }}>Name</TableCell>
                <TableCell sx={{ width: '14%' }}>Client</TableCell>
                <TableCell sx={{ width: '16%' }}>Duration</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>Slots</TableCell>
                <TableCell align="center" sx={{ width: '14%' }}>Impressions</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>Status</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <AssessmentIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />
                      <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                        No Campaigns Yet
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Create your first campaign to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow 
                    key={campaign._id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      transition: 'all 0.2s ease',
                      '& .MuiTableCell-root': {
                        padding: '12px 16px',
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '14%' }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#9ca3af', fontSize: '0.85rem', fontWeight: 500 }}>
                        {campaign.offerNumber || campaign.campaignId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '18%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {campaign.campaignName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '14%', fontSize: '0.9rem', color: '#64748b' }}>
                      {campaign.clientName}
                    </TableCell>
                    <TableCell sx={{ width: '16%', fontSize: '0.9rem', color: '#64748b' }}>
                      {formatDateRange(campaign.startDate, campaign.endDate)}
                    </TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>
                      <Tooltip title={`${campaign.assignedSlots?.length || 0} slots assigned`} arrow>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7C3AED', fontSize: '1rem' }}>
                          {campaign.assignedSlots?.length || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '14%' }}>
                      <Tooltip title={`${campaign.actualImpressions?.toLocaleString() || 0} / ${campaign.estimatedImpressions?.toLocaleString() || 0}`} arrow>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                          {campaign.actualImpressions?.toLocaleString() || 0} / {campaign.estimatedImpressions?.toLocaleString() || 0}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: getSemanticStatusBg(campaign.status),
                          border: `1.5px solid ${getSemanticStatusColor(campaign.status)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: getSemanticStatusColor(campaign.status),
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {getStatusLabel(campaign.status)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setOpenViewDialog(true);
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                              },
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Details" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(campaign)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {canApprove && campaign.status === 'pending_approval' && (
                          <>
                            <Tooltip title="Approve" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedCampaign(campaign);
                                  setOpenApproveDialog(true);
                                }}
                                sx={{
                                  color: '#22c55e',
                                  '&:hover': {
                                    backgroundColor: 'rgba(34, 197, 94, 0.08)',
                                  },
                                }}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedCampaign(campaign);
                                  setOpenRejectDialog(true);
                                }}
                                sx={{
                                  color: '#dc2626',
                                  '&:hover': {
                                    backgroundColor: 'rgba(220, 38, 38, 0.08)',
                                  },
                                }}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {user?.role === 'super_admin' && campaign.status === 'active' && (
                          <Tooltip title="Pause" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handlePauseCampaign(campaign._id)}
                              sx={{
                                color: '#f59e0b',
                                '&:hover': {
                                  backgroundColor: 'rgba(245, 158, 11, 0.08)',
                                },
                              }}
                            >
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {user?.role === 'super_admin' && campaign.status === 'paused' && (
                          <Tooltip title="Resume" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleResumeCampaign(campaign._id)}
                              sx={{
                                color: '#22c55e',
                                '&:hover': {
                                  backgroundColor: 'rgba(34, 197, 94, 0.08)',
                                },
                              }}
                            >
                              <ResumeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {user?.role === 'super_admin' && (
                          <Tooltip title="Update Impressions" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setManualImpressions(campaign.actualImpressions?.toString() || '0');
                                setOpenManualImpressionsDialog(true);
                              }}
                              sx={{
                                color: '#3b82f6',
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                },
                              }}
                            >
                              <StatsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {(user?.role === 'super_admin' || campaign.status === 'draft') ? (
                          <Tooltip title="Delete Campaign" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{
                                color: '#dc2626',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Delete not available for this status" arrow>
                            <IconButton
                              size="small"
                              disabled
                              sx={{
                                color: '#d1d5db',
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)}>
        <DialogTitle>Approve Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve campaign "{selectedCampaign?.campaignName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApproveCampaign} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)}>
        <DialogTitle>Reject Campaign</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Provide a reason for rejecting campaign "{selectedCampaign?.campaignName}":
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectCampaign} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Campaign Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Campaign Name"
              value={editFormData.campaignName}
              onChange={(e) => setEditFormData({ ...editFormData, campaignName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Client Name"
              value={editFormData.clientName}
              onChange={(e) => setEditFormData({ ...editFormData, clientName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Offer Number"
              value={editFormData.offerNumber}
              onChange={(e) => setEditFormData({ ...editFormData, offerNumber: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editFormData.startDate}
                onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editFormData.endDate}
                onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateCampaign} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Details</DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Campaign ID
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedCampaign.campaignId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: getSemanticStatusBg(selectedCampaign.status),
                    border: `1.5px solid ${getSemanticStatusColor(selectedCampaign.status)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: getSemanticStatusColor(selectedCampaign.status),
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {getStatusLabel(selectedCampaign.status)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Campaign Name
                </Typography>
                <Typography variant="body1">{selectedCampaign.campaignName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client Name
                </Typography>
                <Typography variant="body1">{selectedCampaign.clientName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Contact
                </Typography>
                <Typography variant="body1">{selectedCampaign.clientContact}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1">
                  {formatDateRange(selectedCampaign.startDate, selectedCampaign.endDate)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Estimated Impressions
                </Typography>
                <Typography variant="body1">
                  {selectedCampaign.estimatedImpressions?.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Actual Impressions
                </Typography>
                <Typography variant="body1">
                  {selectedCampaign.actualImpressions?.toLocaleString()}
                </Typography>
              </Grid>
              
              {/* Show approval/rejection information */}
              {selectedCampaign.approvalStatus === 'approved' && selectedCampaign.approvedAt && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Approved</strong> on {new Date(selectedCampaign.approvedAt).toLocaleString()}
                    </Typography>
                  </Alert>
                </Grid>
              )}
              
              {selectedCampaign.status === 'rejected' && selectedCampaign.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="body2">
                      <strong>Rejection Reason:</strong> {selectedCampaign.rejectionReason}
                    </Typography>
                  </Alert>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Assigned Slots ({selectedCampaign.assignedSlots?.length || 0})
                </Typography>
                {selectedCampaign.assignedSlots && selectedCampaign.assignedSlots.length > 0 ? (
                  <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedCampaign.assignedSlots.map((slot, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Screen:</strong> {
                            (() => {
                              // Handle if screenName is an object
                              if (typeof slot.screenName === 'object' && slot.screenName !== null) {
                                return slot.screenName.screenName || slot.screenName.siteCode || String(slot.screenName._id || '');
                              }
                              // Handle if slot itself has screen properties
                              if (slot.siteCode) {
                                return slot.siteCode;
                              }
                              // Fallback to string screenName or screenId
                              return slot.screenName || slot.screenId || 'N/A';
                            })()
                          }
                        </Typography>
                        <Typography variant="body2">
                          <strong>Slot:</strong> {slot.slotNumber || 'N/A'} | <strong>Location:</strong> {
                            (() => {
                              // Handle if locationName is an object
                              if (typeof slot.locationName === 'object' && slot.locationName !== null) {
                                return slot.locationName.name || slot.locationName.locationName || String(slot.locationName._id || '');
                              }
                              // Fallback to string locationName
                              return slot.locationName || 'N/A';
                            })()
                          }
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No slots assigned
                  </Typography>
                )}
              </Grid>
              
              {/* Multiple Media Files */}
              {selectedCampaign.mediaFiles && selectedCampaign.mediaFiles.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Media Files ({selectedCampaign.mediaFiles.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedCampaign.mediaFiles.map((media, index) => (
                      <Box
                        key={media._id || index}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          width: '200px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
                          }
                        }}
                        onClick={() => {
                          setSelectedMediaFile(media);
                          setOpenMediaViewerDialog(true);
                        }}
                      >
                        <Typography variant="caption" display="block" fontWeight="bold" mb={0.5}>
                          File {index + 1}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
                          {media.fileName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                          Size: {(media.fileSize / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        {media.thumbnailUrl ? (
                          <img
                            src={`${process.env.REACT_APP_API_URL}${media.thumbnailUrl}`}
                            alt={media.fileName}
                            style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `${process.env.REACT_APP_API_URL}${media.fileUrl}`;
                            }}
                          />
                        ) : media.contentType?.startsWith('image') ? (
                          <img
                            src={`${process.env.REACT_APP_API_URL}${media.fileUrl}`}
                            alt={media.fileName}
                            style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '120px',
                              borderRadius: '4px',
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              fontSize: '12px',
                            }}
                          >
                            📹 {media.fileName}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}
              
              {/* Legacy Single File - Only show if no mediaFiles array */}
              {selectedCampaign.fileUrl && (!selectedCampaign.mediaFiles || selectedCampaign.mediaFiles.length === 0) && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Creative Preview
                  </Typography>
                  {selectedCampaign.contentType?.startsWith('image') ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${selectedCampaign.fileUrl}`}
                      alt="Campaign Creative"
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                    />
                  ) : (
                    <video
                      src={`${process.env.REACT_APP_API_URL}${selectedCampaign.fileUrl}`}
                      controls
                      style={{ maxWidth: '100%', maxHeight: '300px' }}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          
          {/* Show Approve/Reject buttons if user has permission and campaign is pending */}
          {canApprove && selectedCampaign?.status === 'pending_approval' && (
            <>
              <Button
                onClick={() => {
                  setOpenViewDialog(false);
                  setOpenRejectDialog(true);
                }}
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
              >
                Reject
              </Button>
              <Button
                onClick={() => {
                  setOpenViewDialog(false);
                  setOpenApproveDialog(true);
                }}
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Manual Impressions Dialog */}
      <Dialog
        open={openManualImpressionsDialog}
        onClose={() => setOpenManualImpressionsDialog(false)}
      >
        <DialogTitle>Update Manual Impressions</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Update impressions for campaign "{selectedCampaign?.campaignName}":
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Impressions"
            value={manualImpressions}
            onChange={(e) => setManualImpressions(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenManualImpressionsDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateManualImpressions} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Viewer Dialog */}
      <Dialog
        open={openMediaViewerDialog}
        onClose={() => {
          setOpenMediaViewerDialog(false);
          setSelectedMediaFile(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMediaFile?.fileName}
        </DialogTitle>
        <DialogContent>
          {selectedMediaFile?.contentType?.startsWith('image') ? (
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_URL}${selectedMediaFile?.fileUrl}`}
              alt={selectedMediaFile?.fileName}
              sx={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
            />
          ) : (
            <video
              src={`${process.env.REACT_APP_API_URL}${selectedMediaFile?.fileUrl}`}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '600px' }}
            />
          )}
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              File Size: {selectedMediaFile ? (selectedMediaFile.fileSize / 1024 / 1024).toFixed(2) : 0} MB
            </Typography>
            {selectedMediaFile?.contentType?.startsWith('video') && (
              <Typography variant="body2" color="text.secondary">
                Duration: {selectedMediaFile?.duration || 'N/A'} seconds
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenMediaViewerDialog(false);
            setSelectedMediaFile(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Campaign Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete campaign "{selectedCampaign?.campaignName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteCampaign} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Campaigns;